import type {
  DocumentTypeDescriptor,
  FacetItem,
  FilterExpression,
  QueryFieldDescriptor,
  QueryJsonPrimitive,
  QuerySessionState,
} from '@aeppic/query/contracts'

import resolveField from '../../model/query/resolve-field.js'

interface FacetValueState {
  value: QueryJsonPrimitive
  label: string
  count: number
}

export function findFacetField(descriptors: readonly DocumentTypeDescriptor[], path: string): QueryFieldDescriptor | undefined {
  return descriptors.flatMap(descriptor => descriptor.fields).find(field => field.path === path)
}

export function createFacetSession(session: QuerySessionState, field: string, excludeContributionKey?: string): QuerySessionState {
  return {
    ...session,
    toolbarFilter: omitFieldPredicate(session.toolbarFilter, field),
    contributions: Object.fromEntries(Object.entries(session.contributions).map(([key, expression]) => [
      key,
      key === excludeContributionKey ? null : expression,
    ])),
  }
}

export function facetProjection(field: string): string[] {
  if (!field.startsWith('data.')) {
    return []
  }

  return [field.slice('data.'.length).split('.')[0]]
}

export function collectFacetItems(
  documents: readonly unknown[],
  descriptor: QueryFieldDescriptor,
  complete: boolean,
): FacetItem[] {
  const values = new Map<string, FacetValueState>()

  for (const option of descriptor.options ?? []) {
    values.set(valueKey(option.value), { value: option.value, label: option.label, count: 0 })
  }

  for (const document of documents) {
    const documentValues = primitiveValues(resolveField(document as object, descriptor.path))
    if (documentValues.length === 0 && descriptor.missingValue !== undefined) {
      documentValues.push(descriptor.missingValue)
    }

    for (const value of uniqueValues(documentValues)) {
      const key = valueKey(value)
      const state = values.get(key) ?? { value, label: facetLabel(value), count: 0 }
      state.count += 1
      values.set(key, state)
    }
  }

  return [...values.values()].map(state => {
    if (complete) {
      return {
        value: state.value,
        label: state.label,
        usage: state.count > 0 ? 'used' : 'unused',
        count: state.count,
        countAccuracy: 'exact',
      }
    }

    return state.count > 0
      ? {
          value: state.value,
          label: state.label,
          usage: 'used',
          count: state.count,
          countAccuracy: 'lower-bound',
        }
      : {
          value: state.value,
          label: state.label,
          usage: 'unknown',
          countAccuracy: 'unknown',
        }
  })
}

function omitFieldPredicate(expression: FilterExpression | null, field: string): FilterExpression | null {
  if (!expression) {
    return null
  }

  if (expression.kind === 'field') {
    return expression.field === field ? null : expression
  }

  if (expression.kind !== 'group') {
    return expression
  }

  const children = expression.children
    .map(child => omitFieldPredicate(child, field))
    .filter((child): child is FilterExpression => child !== null)

  if (children.length === 0) {
    return null
  }

  return children.length === 1 ? children[0] : { ...expression, children }
}

function primitiveValues(values: unknown[]): QueryJsonPrimitive[] {
  return values.flat(Infinity).filter(isPrimitive)
}

function uniqueValues(values: readonly QueryJsonPrimitive[]): QueryJsonPrimitive[] {
  return [...new Map(values.map(value => [valueKey(value), value])).values()]
}

function valueKey(value: QueryJsonPrimitive): string {
  return `${typeof value}:${JSON.stringify(value)}`
}

function facetLabel(value: QueryJsonPrimitive): string {
  return value === null ? 'None' : String(value)
}

function isPrimitive(value: unknown): value is QueryJsonPrimitive {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value)
}
